import { Configuration, PopupRequest } from '@azure/msal-browser'

// ============================================================================
// MSAL Configuration — Production Safety
// ============================================================================
// This module enforces strict validation:
// - Fails immediately if credentials are missing or contain placeholders
// - Uses window.location.origin for redirectUri (no hardcoded values)
// - Prevents the app from starting with invalid configuration
// ============================================================================

const rawClientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID || ''
const rawTenantId = import.meta.env.VITE_MICROSOFT_TENANT_ID || ''
const rawApiScope = import.meta.env.VITE_MICROSOFT_API_SCOPE || ''

// Placeholder detection patterns
const PLACEHOLDER_PATTERNS = [
  'your-entra-tenant-id',
  'your-app-registration-client-id',
  'your-',
  'placeholder',
  'example',
  'change_me',
  'replace_me',
  'dummy',
]

function isPlaceholder(value: string): boolean {
  const lowerValue = value.toLowerCase()
  return PLACEHOLDER_PATTERNS.some(pattern => lowerValue.includes(pattern))
}

function validateMsalConfig(clientId: string, tenantId: string): void {
  const errors: string[] = []

  if (!clientId) {
    errors.push('VITE_MICROSOFT_CLIENT_ID is missing from environment variables.')
  } else if (isPlaceholder(clientId)) {
    errors.push(`VITE_MICROSOFT_CLIENT_ID contains a placeholder: "${clientId}". Use real Azure App Registration credentials.`)
  }

  if (!tenantId) {
    errors.push('VITE_MICROSOFT_TENANT_ID is missing from environment variables.')
  } else if (isPlaceholder(tenantId)) {
    errors.push(`VITE_MICROSOFT_TENANT_ID contains a placeholder: "${tenantId}". Use real Entra ID tenant credentials.`)
  }

  if (errors.length > 0) {
    const fullMessage = [
      '❌ MSAL CONFIGURATION ERROR',
      ...errors,
      '',
      'The application cannot proceed without valid Microsoft Entra ID credentials.',
      'Please configure VITE_MICROSOFT_CLIENT_ID and VITE_MICROSOFT_TENANT_ID in your .env.local file.',
    ].join('\n')

    console.error(fullMessage)
    throw new Error(fullMessage)
  }
}

// Validate before building config
validateMsalConfig(rawClientId, rawTenantId)

const clientId = rawClientId
const tenantId = rawTenantId
const redirectUri = window.location.origin

/**
 * Destino del `redirect_uri` que usa EXCLUSIVAMENTE la renovación silenciosa
 * (`acquireTokenSilent` → iframe oculto). Se mantiene separado de `redirectUri`
 * a propósito: el login interactivo debe seguir aterrizando en la SPA, donde
 * `handleRedirectPromise()` procesa la respuesta.
 *
 * MSAL v5 entrega la respuesta del iframe por BroadcastChannel, y solo la
 * publica la página que llama a `broadcastResponseToMainFrame()`. Apuntar el
 * flujo silencioso al origen de la SPA hacía que la aplicación entera
 * arrancara dentro del iframe oculto: nunca publicaba nada, el frame padre
 * agotaba `iframeBridgeTimeout` y rechazaba con `timed_out`, y la copia
 * enmarcada pedía a su vez otro token silencioso. El HAR capturado en
 * Edge/Windows registró esa recursión: seis `/authorize` en ~11 s, con el
 * bundle completo recargándose entre medio.
 *
 * Debe estar registrado en Entra ID como redirect URI de tipo SPA.
 */
export const silentRedirectUri = `${redirectUri}/auth/silent-callback.html`

export const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    postLogoutRedirectUri: redirectUri,
  },
  cache: {
    cacheLocation: 'localStorage',
  },
}

export const graphScopes: PopupRequest = {
  scopes: ['User.Read'],
}

export const apiScopes: PopupRequest = {
  scopes: rawApiScope ? [rawApiScope] : [],
}

export const isApiScopeConfigured = () => Boolean(rawApiScope && !isPlaceholder(rawApiScope))

export const isMsalConfigured = () =>
  Boolean(clientId && tenantId && !isPlaceholder(clientId) && !isPlaceholder(tenantId))
